import React, { useState } from 'react';
import { CalendarIcon, PlayIcon, CheckCircleIcon, ExclamationTriangleIcon, EllipsisHorizontalIcon, PencilIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import Button from '../../../../ui/canon/Button.jsx';
import { getAvatarStyle } from '../../lib/s3.js';

const SprintCloseModal = ({ isOpen, sprint, onClose, onConfirm }) => {
  const [notes, setNotes] = useState('');
  if (!isOpen || !sprint) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-succession-bg-primary rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-succession-text-primary mb-4">Close Sprint</h3>
          <textarea
            className="w-full px-3 py-2 border border-succession-border rounded-md focus:outline-none focus:ring-2 focus:ring-succession-focus-ring focus:border-succession-primary text-succession-text-primary bg-succession-bg-primary"
            rows={4}
            placeholder="Retrospective notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="flex items-center justify-end gap-3 mt-6">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={() => onConfirm(notes)} className="bg-orange-600 hover:bg-orange-700">Close Sprint</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SprintListTable = ({ sprints = [], onEdit, onClose, onOpen, className = '' }) => {
  const [closeModalSprint, setCloseModalSprint] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const formatDateRange = (startOn, endOn) => {
    try {
      const start = new Date(startOn);
      const end = new Date(endOn);
      const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startStr} - ${endStr}`;
    } catch (_e) {
      return '';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PLANNED: { color: 'bg-blue-100 text-blue-800', icon: CalendarIcon },
      ACTIVE: { color: 'bg-green-100 text-green-800', icon: PlayIcon },
      CLOSED: { color: 'bg-gray-100 text-gray-800', icon: CheckCircleIcon },
      ABANDONED: { color: 'bg-red-100 text-red-800', icon: ExclamationTriangleIcon },
    };

    const normalizedStatus = status?.toUpperCase();
    const config = statusConfig[normalizedStatus] || statusConfig.PLANNED;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        <Icon className="w-3 h-3" />
        {normalizedStatus?.charAt(0) + normalizedStatus?.slice(1).toLowerCase()}
      </span>
    );
  };

  const resolveAvatarSrc = (val) => {
    if (!val) return getAvatarStyle('advisor-square.png').url;
    if (val.startsWith('http')) return val;
    if (val.startsWith('/')) return val;
    return getAvatarStyle(val).url;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getBgColorForName = (name) => {
    if (!name) return '#9ca3af';
    const colors = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const handleCloseConfirm = (notes) => {
    if (closeModalSprint && onClose) {
      onClose({ ...closeModalSprint, retrospectiveNotes: notes });
    }
    setCloseModalSprint(null);
  };

  if (!sprints || sprints.length === 0) {
    return (
      <div className="text-center py-12 bg-succession-bg-primary rounded-lg border border-succession-border">
        <div className="text-succession-text-muted text-6xl mb-4">🏃‍♂️</div>
        <h3 className="text-lg font-medium text-succession-text-primary mb-2">No sprints found</h3>
        <p className="text-succession-text-secondary">Create your first sprint to get started</p>
      </div>
    );
  }

  return (
    <>
      <div className={`bg-succession-bg-primary rounded-lg border border-succession-border shadow-sm overflow-hidden ${className}`}>
        {/* Desktop Table */}
        <div className="block overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-succession-bg-secondary border-b border-succession-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">Sprint</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">Dates</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">Issues</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">Coach</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-succession-text-secondary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
              {sprints.map((sprint) => {
                const metrics = sprint.metricsSnapshot || {};
                const completionPct = metrics.completionPct || 0;
                const completedItems = metrics.completedItems || 0;
                const totalItems = metrics.totalItems || 0;
                const overdueCount = sprint.overdueCount || 0;
                const blockedCount = sprint.blockedCount || 0;
                const isCoachLed = sprint.sourceType === 'SprintProgram';
                const coach = sprint.coachProfile;

                return (
                  <tr key={sprint.id} className="hover:bg-succession-hover-bg">
                    <td className="px-6 py-4">
                      <div>
                        <button
                          onClick={() => onOpen && onOpen(sprint)}
                          className="font-medium text-succession-text-primary hover:text-succession-primary transition-colors cursor-pointer text-left"
                        >
                          {sprint.name}
                        </button>
                        {sprint.objectiveText && (
                          <div className="text-sm text-succession-text-secondary max-w-xs truncate">{sprint.objectiveText}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-succession-text-primary">
                      {formatDateRange(sprint.startOn, sprint.endOn)}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(sprint.status)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-succession-bg-secondary rounded-full h-2">
                          <div
                            className="bg-succession-primary h-2 rounded-full"
                            style={{ width: `${completionPct}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-succession-text-primary">{completionPct}%</span>
                      </div>
                      <div className="text-xs text-succession-text-secondary mt-1">
                        {completedItems}/{totalItems} items
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 text-sm">
                        {overdueCount > 0 && (
                          <span className="text-succession-error font-medium">
                            {overdueCount} overdue
                          </span>
                        )}
                        {blockedCount > 0 && (
                          <span className="text-succession-warning font-medium">
                            {blockedCount} blocked
                          </span>
                        )}
                        {overdueCount === 0 && blockedCount === 0 && (
                          <span className="text-succession-text-muted">No issues</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {isCoachLed && coach ? (
                        <div className="flex items-center gap-2">
                          {coach.avatar_url || coach.avatarUrl ? (
                            <img
                              src={resolveAvatarSrc(coach.avatar_url || coach.avatarUrl)}
                              alt={coach.name}
                              className="w-6 h-6 rounded-full"
                            />
                          ) : (
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white"
                              style={{ backgroundColor: getBgColorForName(coach.name) }}
                            >
                              {getInitials(coach.name)}
                            </div>
                          )}
                          <span className="text-sm text-succession-text-primary">{coach.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-succession-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setDropdownOpen(dropdownOpen === sprint.id ? null : sprint.id)}
                          className="p-1 rounded-md hover:bg-succession-hover-bg text-succession-text-secondary hover:text-succession-text-primary"
                        >
                          <EllipsisHorizontalIcon className="w-5 h-5" />
                        </button>
                        {dropdownOpen === sprint.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setDropdownOpen(null)}
                            />
                            <div className="absolute right-0 mt-1 w-48 bg-succession-bg-primary border border-succession-border rounded-md shadow-lg z-20">
                              <button
                                onClick={() => {
                                  onOpen && onOpen(sprint);
                                  setDropdownOpen(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-succession-text-primary hover:bg-succession-hover-bg flex items-center gap-2"
                              >
                                <PlayIcon className="w-4 h-4" />
                                Open
                              </button>
                              <button
                                onClick={() => {
                                  onEdit && onEdit(sprint);
                                  setDropdownOpen(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-succession-text-primary hover:bg-succession-hover-bg flex items-center gap-2"
                              >
                                <PencilIcon className="w-4 h-4" />
                                Edit
                              </button>
                              {sprint.status === 'ACTIVE' && (
                                <>
                                  <div className="border-t border-succession-border my-1" />
                                  <button
                                    onClick={() => {
                                      setCloseModalSprint(sprint);
                                      setDropdownOpen(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-succession-error hover:bg-succession-error-bg flex items-center gap-2"
                                  >
                                    <ArchiveBoxIcon className="w-4 h-4" />
                                    Close Sprint
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-succession-border">
          {sprints.map((sprint) => {
            const metrics = sprint.metricsSnapshot || {};
            const completionPct = metrics.completionPct || 0;
            const completedItems = metrics.completedItems || 0;
            const totalItems = metrics.totalItems || 0;
            const overdueCount = sprint.overdueCount || 0;
            const blockedCount = sprint.blockedCount || 0;
            const isCoachLed = sprint.sourceType === 'SprintProgram';
            const coach = sprint.coachProfile;

            return (
              <div key={sprint.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <button
                      onClick={() => onOpen && onOpen(sprint)}
                      className="font-medium text-succession-text-primary hover:text-succession-primary transition-colors cursor-pointer text-left block"
                    >
                      {sprint.name}
                    </button>
                    <p className="text-sm text-succession-text-secondary mt-1">{sprint.objectiveText}</p>
                  </div>
                  <button
                    onClick={() => setDropdownOpen(dropdownOpen === sprint.id ? null : sprint.id)}
                    className="p-1 rounded-md hover:bg-succession-hover-bg text-succession-text-secondary"
                  >
                    <EllipsisHorizontalIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-succession-text-secondary">Status:</span>
                    {getStatusBadge(sprint.status)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-succession-text-secondary">Dates:</span>
                    <span className="text-sm text-succession-text-primary">
                      {formatDateRange(sprint.startOn, sprint.endOn)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-succession-text-secondary">Progress:</span>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-succession-bg-secondary rounded-full h-2">
                        <div
                          className="bg-succession-primary h-2 rounded-full"
                          style={{ width: `${completionPct}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-succession-text-primary">{completionPct}%</span>
                    </div>
                  </div>
                  {isCoachLed && coach && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-succession-text-secondary">Coach:</span>
                      <div className="flex items-center gap-2">
                        {coach.avatar_url || coach.avatarUrl ? (
                          <img
                            src={resolveAvatarSrc(coach.avatar_url || coach.avatarUrl)}
                            alt={coach.name}
                            className="w-5 h-5 rounded-full"
                          />
                        ) : (
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium text-white"
                            style={{ backgroundColor: getBgColorForName(coach.name) }}
                          >
                            {getInitials(coach.name)}
                          </div>
                        )}
                        <span className="text-sm text-succession-text-primary">{coach.name}</span>
                      </div>
                    </div>
                  )}
                  {(overdueCount > 0 || blockedCount > 0) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-succession-text-secondary">Issues:</span>
                      <div className="flex items-center gap-2 text-sm">
                        {overdueCount > 0 && (
                          <span className="text-succession-error font-medium">{overdueCount} overdue</span>
                        )}
                        {blockedCount > 0 && (
                          <span className="text-succession-warning font-medium">{blockedCount} blocked</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <SprintCloseModal
        isOpen={!!closeModalSprint}
        sprint={closeModalSprint}
        onClose={() => setCloseModalSprint(null)}
        onConfirm={handleCloseConfirm}
      />
    </>
  );
};

export default SprintListTable;
